/** Aufträge: Liste mit Filtern in der URL und Auftragsdetail (/auftraege/:id). */
import { useEffect, useState, type FormEvent } from 'react';
import { Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AUFTRAG_STATUS, KLAERFALL_GRUENDE, ROUTEN, SENDUNG_STATUS, UMSCHALT_TEXT, UMSCHALT_TEXT_LABEL, versandproduktText, type KlaerfallGrund } from '@storeos/contracts';
import { stornoZurueckgelegt } from '../api/packstation';
import * as a from '../api/auftraege';
import type { Adresse, SendungDetail } from '../api/typen';
import { belegPdfPfad, gutschriftAnlegen, labelPdfPfad, type ErstattungswegT } from '../api/belege';
import { BelegKlaerfaelle } from '../components/BelegKlaerfaelle';
import { DateiLink } from '../components/DateiLink';
import { VersandwahlSchatten } from '../components/Versandwahl';
import { AUFTRAG_TEXT, datum, datumZeit, euro, KLAERFALL_TEXT, ROUTE_TEXT, SENDUNG_TEXT, STEUERFALL_TEXT } from '../components/format';
import { AuftragBadge, RetoureBadge, RouteBadge, SendungBadge, TestBadge } from '../components/Status';
import { Darf, Dialog, DialogFormular, Feld, Filterleiste, Karte, Seitenkopf, Tabelle, useDarf, useToast, Zustand } from '../components/ui';
import { UmschaltBestaetigenDialog } from './Umschaltung';

export function Auftraege() {
  return (
    <Routes>
      <Route index element={<AuftragListe />} />
      <Route path=":id" element={<AuftragDetail />} />
    </Routes>
  );
}

const PRO_SEITE = 50;

function AuftragListe() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const f = Object.fromEntries(sp.entries()) as Record<string, string>;
  const seite = Number(f.seite ?? 1);
  const [suche, setSuche] = useState(f.q ?? '');
  useEffect(() => setSuche(f.q ?? ''), [f.q]);
  const q = useQuery({ queryKey: ['auftraege', f], queryFn: () => a.auftraege({ ...f, seite, pro_seite: PRO_SEITE }), placeholderData: keepPreviousData });

  const setze = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    n.delete('seite');
    setSp(n, { replace: true });
  };

  async function suchen(e: FormEvent) {
    e.preventDefault();
    const s = suche.trim();
    if (/^\d{4,}$/.test(s)) {
      const r = await a.auftraege({ q: s, pro_seite: 5 });
      const genau = r.eintraege.find((z) => z.bestellnummer === s);
      if (genau) { nav(`/auftraege/${genau.id}`); return; }
    }
    setze('q', s);
  }

  const gesamt = q.data?.gesamt ?? 0;
  const seiten = Math.max(1, Math.ceil(gesamt / PRO_SEITE));
  return (
    <section>
      <Seitenkopf titel="Aufträge" unter={q.data ? `${gesamt} Aufträge` : undefined} />
      <Filterleiste>
        <form onSubmit={suchen} className="suche">
          <input type="search" value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Bestellnr., Name, E-Mail, Tracking" aria-label="Suche" data-testid="auftrag-suche" />
          <button type="submit" className="knopf">Suchen</button>
        </form>
        <select aria-label="Auftragsstatus" value={f.status ?? ''} onChange={(e) => setze('status', e.target.value)}>
          <option value="">Auftragsstatus: alle</option>
          {AUFTRAG_STATUS.map((s) => <option key={s} value={s}>{AUFTRAG_TEXT[s]}</option>)}
        </select>
        <select aria-label="Sendungsstatus" value={f.sendung_status ?? ''} onChange={(e) => setze('sendung_status', e.target.value)} data-testid="filter-sendung">
          <option value="">Sendungsstatus: alle</option>
          {SENDUNG_STATUS.map((s) => <option key={s} value={s}>{SENDUNG_TEXT[s]}</option>)}
        </select>
        <select aria-label="Route" value={f.route ?? ''} onChange={(e) => setze('route', e.target.value)}>
          <option value="">Route: alle</option>
          {ROUTEN.map((r) => <option key={r} value={r}>{ROUTE_TEXT[r]}</option>)}
        </select>
        <select aria-label="Land" value={f.land ?? ''} onChange={(e) => setze('land', e.target.value)}>
          <option value="">Land: alle</option>
          {['DE', 'AT'].map((l) => <option key={l}>{l}</option>)}
        </select>
        <label className="inline">von <input type="date" value={f.von ?? ''} onChange={(e) => setze('von', e.target.value)} /></label>
        <label className="inline">bis <input type="date" value={f.bis ?? ''} onChange={(e) => setze('bis', e.target.value)} /></label>
        {sp.toString() && <button type="button" className="knopf-flach" onClick={() => setSp(new URLSearchParams(), { replace: true })}>Filter zurücksetzen</button>}
      </Filterleiste>
      <Zustand laden={q.isLoading} fehler={q.error}>
        <Tabelle testid="auftrag-tabelle" zeilen={q.data?.eintraege ?? []} schluessel={(z) => z.id} onZeile={(z) => nav(`/auftraege/${z.id}`)} leer="Keine Aufträge für diese Filter"
          spalten={[
            { kopf: 'Bestellnr.', zelle: (z) => <span className="mono">{z.bestellnummer}{z.ist_test && <> <span className="badge env-test">TEST</span></>}</span> },
            { kopf: 'Bestellt', zelle: (z) => datumZeit(z.bestellt_at) },
            { kopf: 'Kunde', zelle: (z) => <span className="kuerzen">{z.kunde_name}<br /><small className="hinweis">{z.kunde_email}</small></span> },
            { kopf: 'Land', zelle: (z) => z.lieferland },
            { kopf: 'Status', zelle: (z) => <AuftragBadge status={z.status} /> },
            { kopf: 'Sendungen', zelle: (z) => <span className="badge-reihe">{z.sendungen.map((s) => <span key={s.id} className="sendung-mini"><SendungBadge status={s.status} />{s.klaerfall_grund && <small> {KLAERFALL_TEXT[s.klaerfall_grund]}</small>}</span>)}</span> },
            { kopf: 'Route', zelle: (z) => <span className="badge-reihe">{z.routen.map((r) => <RouteBadge key={r} route={r} />)}</span> },
            { kopf: 'Summe', zelle: (z) => euro(z.summe_brutto_cents), rechts: true },
          ]} />
        {seiten > 1 && (
          <div className="blaettern">
            <button type="button" className="knopf-zweit" disabled={seite <= 1} onClick={() => { const n = new URLSearchParams(sp); n.set('seite', String(seite - 1)); setSp(n); }}>Zurück</button>
            <span>Seite {seite} von {seiten}</span>
            <button type="button" className="knopf-zweit" disabled={seite >= seiten} onClick={() => { const n = new URLSearchParams(sp); n.set('seite', String(seite + 1)); setSp(n); }}>Weiter</button>
          </div>
        )}
      </Zustand>
    </section>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────

function AuftragDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const darf = useDarf();
  const q = useQuery({ queryKey: ['auftrag', id], queryFn: () => a.auftrag(id) });
  const tl = useQuery({ queryKey: ['auftrag', id, 'timeline'], queryFn: () => a.auftragTimeline(id) });
  const [adressDialog, setAdressDialog] = useState<Adresse | null>(null);
  const [klaerDialog, setKlaerDialog] = useState<SendungDetail | null>(null);
  const [loesenDialog, setLoesenDialog] = useState<SendungDetail | null>(null);
  const [trackingDialog, setTrackingDialog] = useState<SendungDetail | null>(null);
  const [gutschrift, setGutschrift] = useState(false);
  const [umschaltung, setUmschaltung] = useState(false);
  const neu = () => { void qc.invalidateQueries({ queryKey: ['auftrag', id] }); void qc.invalidateQueries({ queryKey: ['auftraege'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); };

  const o = q.data;
  if (!o) return <Zustand laden={q.isLoading} fehler={q.error} />;
  const positionVon = (oid: string) => o.positionen.find((p) => p.id === oid);
  const rechnung = o.belege.find((b) => b.art === 'rechnung');

  return (
    <section className="detail">
      <Seitenkopf titel={`Auftrag ${o.bestellnummer}`}
        unter={<span className="badge-reihe"><AuftragBadge status={o.status} /> {o.ist_test && <span className="badge env-test">Testauftrag</span>} <span>Kanal: {o.kanal_status}</span> <span>bestellt {datumZeit(o.bestellt_at)}</span> <span>bezahlt {datumZeit(o.bezahlt_at)}</span></span>}>
        <button type="button" className="knopf-zweit" onClick={() => nav(-1)}>Zurück</button>
      </Seitenkopf>

      <div className="raster-detail">
        <div className="spalte">
          <Karte titel="Positionen">
            <Tabelle zeilen={o.positionen} schluessel={(p) => p.id}
              spalten={[
                { kopf: '#', zelle: (p) => p.position },
                { kopf: 'SKU', zelle: (p) => p.sku_snapshot ?? '–', klasse: 'mono' },
                { kopf: 'Artikel', zelle: (p) => <>{p.name_snapshot}{p.variante_snapshot && <small className="hinweis"> · {p.variante_snapshot}</small>}</> },
                { kopf: 'Route', zelle: (p) => <RouteBadge route={p.route} /> },
                { kopf: 'Produktion', zelle: (p) => (p.produktions_status === 'nicht_noetig' ? '–' : p.produktions_status) },
                { kopf: 'Menge', zelle: (p) => p.menge, rechts: true },
                { kopf: 'Einzel', zelle: (p) => euro(p.einzel_brutto_cents), rechts: true },
                { kopf: 'Gesamt', zelle: (p) => euro(p.gesamt_brutto_cents), rechts: true },
              ]} />
            <dl className="summen">
              <dt>Versand</dt><dd>{euro(o.versand_brutto_cents)}</dd>
              {o.rabatt_brutto_cents > 0 && <><dt>Rabatt</dt><dd>−{euro(o.rabatt_brutto_cents)}</dd></>}
              <dt>Summe</dt><dd><strong>{euro(o.summe_brutto_cents)}</strong></dd>
              {o.erstattet_cents > 0 && <><dt>Erstattet</dt><dd>{euro(o.erstattet_cents)}</dd></>}
              <dt>Zahlart</dt><dd>{o.zahlart_titel ?? o.zahlart ?? '–'}</dd>
              <dt>Steuerfall</dt><dd>{o.steuerfall ? (STEUERFALL_TEXT[o.steuerfall] ?? o.steuerfall) : <span className="warn-text">unbekannt (Land prüfen)</span>} · {o.lieferland || '–'}</dd>
            </dl>
            {o.kundenhinweis && <p className="hinweis-box">Kundenhinweis: {o.kundenhinweis}</p>}
          </Karte>

          <Karte titel={`Sendungen (${o.sendungen.length})`}>
            {o.sendungen.length === 0 && <p className="hinweis">Keine Sendung (unbezahlt oder Testauftrag).</p>}
            {o.sendungen.map((s) => {
              const lagerAnteil = s.positionen.some((sp) => positionVon(sp.order_item_id)?.route === 'lager');
              const internAnteil = s.positionen.some((sp) => positionVon(sp.order_item_id)?.route === 'intern_bb');
              return (
                <div key={s.id} className="sendung" data-testid="sendung" data-status={s.status}>
                  <div className="sendung-kopf">
                    <strong className="mono">{o.bestellnummer}-{s.nummer}</strong>
                    <SendungBadge status={s.status} />
                    <span className="hinweis">{s.route === 'partner' ? `Partner${s.partner_name ? `: ${s.partner_name}` : ''}` : 'Lager'}{s.versandprodukt ? ` · ${versandproduktText(s.versandprodukt)}` : ''}{s.gewicht_g ? ` · ${(s.gewicht_g / 1000).toFixed(2).replace('.', ',')} kg` : ''}</span>
                    <span className="aktionen">
                      {!['uebergeben', 'zugestellt', 'zustellproblem', 'storniert', 'klaerfall'].includes(s.status) && <button type="button" className="knopf-zweit klein" onClick={() => setKlaerDialog(s)}>Klärfall melden</button>}
                      {s.status === 'klaerfall' && (s.klaerfall_text ?? '').startsWith('STORNO')
                        ? <button type="button" className="knopf-gefahr klein" data-testid="storno-erledigt" onClick={async () => {
                          try { await stornoZurueckgelegt(s.id); toast('Storno erledigt: Ware zurück im Fach, Sendung storniert'); void qc.invalidateQueries(); } catch (e) { toast((e as Error).message, 'fehler'); }
                        }}>Storno: Ware ist zurück im Fach</button>
                        : s.status === 'klaerfall' && s.klaerfall_text === UMSCHALT_TEXT && darf('leitung')
                          ? <button type="button" className="knopf klein" data-testid="umschaltung-bestaetigen" onClick={() => setUmschaltung(true)}>Umschaltung bestätigen</button>
                          : s.status === 'klaerfall' && darf('leitung') && <button type="button" className="knopf klein" onClick={() => setLoesenDialog(s)}>Klärfall lösen</button>}
                      {s.status === 'an_partner' && <button type="button" className="knopf-zweit klein" onClick={() => setTrackingDialog(s)}>Tracking nachtragen</button>}
                    </span>
                  </div>
                  {s.status === 'klaerfall' && <p className="klaerfall-box">Klärfall {s.klaerfall_grund ? KLAERFALL_TEXT[s.klaerfall_grund] : ''}: {s.klaerfall_text ?? '–'}{s.status_vor_klaerfall && <> · zurück nach <SendungBadge status={s.status_vor_klaerfall} /></>}</p>}
                  {s.status === 'wartet_produktion' && lagerAnteil && internAnteil && <p className="hinweis">Lagerware wartet mit dem personalisierten Produkt, beides geht in einem Paket.</p>}
                  {s.status === 'wartet_bestand' && <p className="hinweis" data-testid="hinweis-kein-split">Die Sendung wartet komplett, bis alle Lagerware da ist. Teillieferungen gibt es nicht.</p>}
                  {s.status === 'klaerfall' && s.klaerfall_text === UMSCHALT_TEXT && <p className="hinweis" data-testid="hinweis-umschaltung">Umschalttag: Dieser Auftrag kam im Parallelbetrieb mit Billbee. Prüfe in Billbee, ob er dort versandt wurde, und bestätige dann, wer versendet. Erst danach kommt er an die Packstation.</p>}
                  {s.status === 'klaerfall' && s.klaerfall_text === UMSCHALT_TEXT_LABEL && <p className="hinweis" data-testid="hinweis-umschaltung-label">Umschalttag: Billbee versendet diesen Auftrag, in StoreOS liegt schon ein Label. Label beim Versanddienst stornieren, Ware zurück an den Platz, dann „Klärfall lösen“.</p>}
                  {s.status === 'klaerfall' && s.klaerfall_grund === 'ausland' && <p className="hinweis" data-testid="hinweis-ausland">Versendet wird nur nach Deutschland. Die Leitung entscheidet: Klärfall lösen = ausnahmsweise versenden, sonst im Shop stornieren. Im Shop ändert StoreOS nichts.</p>}
                  <ul className="sendung-pos">
                    {s.positionen.map((sp) => { const p = positionVon(sp.order_item_id); return <li key={sp.id}>{sp.menge}× {p?.name_snapshot}{p?.variante_snapshot ? ` · ${p.variante_snapshot}` : ''} <small className="hinweis">({sp.gepackt_menge}/{sp.menge} gepackt{sp.override_grund ? `, Override: ${sp.override_grund}` : ''})</small></li>; })}
                  </ul>
                  {s.route === 'lager' && <VersandwahlSchatten sendungId={s.id} />}
                  {s.pakete.map((pk) => (
                    <div key={pk.id} className="paket">
                      <span className="mono" data-testid="tracking-nummer">{pk.tracking_nummer}</span>
                      <span className="badge neutral">{pk.carrier}{pk.ist_mock ? ' · MOCK' : ''}</span>
                      <span className="hinweis">{pk.carrier_status}{pk.storniert_at ? ' · storniert' : ''}</span>
                      {pk.hat_label_pdf && <DateiLink pfad={labelPdfPfad(pk.id)} className="knopf-flach">Label-PDF</DateiLink>}
                      {pk.tracking_url && <a href={pk.tracking_url} target="_blank" rel="noopener noreferrer" className="knopf-flach">Tracking</a>}
                    </div>
                  ))}
                </div>
              );
            })}
          </Karte>

          <Karte titel="Timeline">
            <Zustand laden={tl.isLoading} fehler={tl.error}>
              <ol className="timeline" data-testid="timeline">
                {(tl.data?.eintraege ?? []).map((t, i) => (
                  <li key={i} className={`tl-${t.quelle}`}>
                    <time>{datumZeit(t.at)}</time>
                    <span className="tl-titel">{t.titel}{t.text && <span className="tl-text hinweis">{t.text}</span>}</span>
                    <span className="tl-wer">{t.benutzer ?? ''}</span>
                  </li>
                ))}
                {tl.data?.eintraege.length === 0 && <li className="hinweis">Keine Einträge</li>}
              </ol>
            </Zustand>
          </Karte>
        </div>

        <div className="spalte schmal">
          {(['lieferung', 'rechnung'] as const).map((typ) => {
            const ad = o.adressen.find((x) => x.typ === typ);
            if (!ad) return null;
            return (
              <Karte key={typ} titel={typ === 'lieferung' ? 'Lieferadresse' : 'Rechnungsadresse'}
                aktion={<Darf rolle="leitung"><button type="button" className="knopf-zweit klein" onClick={() => setAdressDialog(ad)} data-testid={`adresse-korrigieren-${typ}`}>Korrigieren</button></Darf>}>
                <address className="adresse" data-testid={`adresse-${typ}`}>
                  {ad.firma && <>{ad.firma}<br /></>}
                  {ad.vorname} {ad.nachname}<br />
                  {ad.strasse} {ad.hausnummer || <span className="warn-text">[Hausnummer fehlt]</span>}{ad.zusatz && <><br />{ad.zusatz}</>}<br />
                  {ad.plz} {ad.ort}<br />{ad.land}
                </address>
                <div className="badge-reihe">
                  <span className="badge neutral" data-testid={`adresse-version-${typ}`}>Version {ad.version}</span>
                  <span className={`badge ${ad.parse_qualitaet === 'ok' || ad.parse_qualitaet === 'manuell' ? 'neutral' : 'warn'}`}>Parser: {ad.parse_qualitaet}</span>
                </div>
                <p className="hinweis">Original: {ad.roh_adresse1}{ad.roh_adresse2 ? `, ${ad.roh_adresse2}` : ''}</p>
                {typ === 'rechnung' && o.kunde_email && <p className="hinweis">{o.kunde_email}</p>}
              </Karte>
            );
          })}

          <Darf rolle="leitung">
            <Karte titel="Belege" aktion={rechnung && <button type="button" className="knopf-zweit klein" onClick={() => setGutschrift(true)}>Gutschrift</button>}>
              {o.belege.length === 0 && <p className="hinweis">Noch keine Belege.</p>}
              <ul className="liste-einfach">
                {o.belege.map((b) => (
                  <li key={b.id}>
                    <DateiLink pfad={belegPdfPfad(b.id)} className="mono">{b.nummer}</DateiLink>
                    <span>{b.art === 'rechnung' ? 'Rechnung' : b.art === 'gutschrift' ? 'Gutschrift' : 'Lieferschein'} · {datum(b.datum)}</span><span className="rechts">{b.art === 'lieferschein' ? '' : euro(b.brutto_cents)}</span><TestBadge test={b.ist_test} />
                  </li>
                ))}
              </ul>
            </Karte>
          </Darf>

          <Darf rolle="leitung"><BelegKlaerfaelle orderId={o.id} /></Darf>

          {o.retouren.length > 0 && (
            <Karte titel="Retouren">
              <ul className="liste-einfach">
                {o.retouren.map((r) => <li key={r.id}><span className="mono">{r.nummer}</span><RetoureBadge status={r.status} /><span>{datum(r.angemeldet_at)}</span></li>)}
              </ul>
            </Karte>
          )}
        </div>
      </div>

      <AdressDialog adresse={adressDialog} orderId={o.id} onSchliessen={() => setAdressDialog(null)} onFertig={(h) => { setAdressDialog(null); toast(h.length ? `Adresse gespeichert. ${h.join(' · ')}` : 'Adresse gespeichert'); neu(); }} />
      <KlaerfallMeldenDialog sendung={klaerDialog} onSchliessen={() => setKlaerDialog(null)} onFertig={() => { setKlaerDialog(null); toast('Klärfall gemeldet'); neu(); }} />
      <Dialog offen={!!loesenDialog} titel="Klärfall lösen" onSchliessen={() => setLoesenDialog(null)}>
        <LoesenFormular sendung={loesenDialog} onAbbrechen={() => setLoesenDialog(null)} onFertig={() => { setLoesenDialog(null); toast('Klärfall gelöst'); neu(); }} />
      </Dialog>
      <Dialog offen={!!trackingDialog} titel="Partner-Tracking nachtragen" onSchliessen={() => setTrackingDialog(null)}>
        {trackingDialog && <PartnerTrackingFormular sendungId={trackingDialog.id} onAbbrechen={() => setTrackingDialog(null)} onFertig={() => { setTrackingDialog(null); toast('Tracking gespeichert, Sendung übergeben'); neu(); }} />}
      </Dialog>
      <UmschaltBestaetigenDialog zeile={umschaltung ? { orderId: o.id, bestellnummer: o.bestellnummer, sendungen: o.sendungen.map((s) => SENDUNG_TEXT[s.status]) } : null}
        onSchliessen={() => setUmschaltung(false)} onFertig={(text) => { setUmschaltung(false); toast(text); neu(); }} />
      <Dialog offen={gutschrift} titel="Gutschrift anlegen" onSchliessen={() => setGutschrift(false)}>
        <GutschriftFormular orderId={o.id} vorschlag={rechnung ? rechnung.brutto_cents - o.erstattet_cents : 0} onAbbrechen={() => setGutschrift(false)} onFertig={(nr) => { setGutschrift(false); toast(`Gutschrift ${nr} angelegt`); neu(); void qc.invalidateQueries({ queryKey: ['belege'] }); }} />
      </Dialog>
    </section>
  );
}

function AdressDialog({ adresse, orderId, onSchliessen, onFertig }: { adresse: Adresse | null; orderId: string; onSchliessen: () => void; onFertig: (hinweise: string[]) => void }) {
  return (
    <Dialog offen={!!adresse} titel={`Adresse korrigieren (Version ${adresse?.version ?? ''})`} onSchliessen={onSchliessen} breit>
      {adresse && <AdressFormular key={`${adresse.id}-${adresse.version}`} adresse={adresse} orderId={orderId} onSchliessen={onSchliessen} onFertig={onFertig} />}
    </Dialog>
  );
}

/** Werte synchron aus der Adresse (kein Effekt: sonst überschreibt ein schneller Tipper/Scanner die Vorbelegung). */
function AdressFormular({ adresse, orderId, onSchliessen, onFertig }: { adresse: Adresse; orderId: string; onSchliessen: () => void; onFertig: (hinweise: string[]) => void }) {
  const [w, setW] = useState<Record<string, string>>(() => ({
    vorname: adresse.vorname, nachname: adresse.nachname, firma: adresse.firma ?? '', strasse: adresse.strasse, hausnummer: adresse.hausnummer,
    zusatz: adresse.zusatz ?? '', plz: adresse.plz, ort: adresse.ort, land: adresse.land,
  }));
  const [grund, setGrund] = useState('');
  const feld = (k: string, label: string, breit = false) => (
    <Feld label={label}><input className={breit ? 'breit' : ''} value={w[k] ?? ''} onChange={(e) => { const v = e.target.value; setW((alt) => ({ ...alt, [k]: v })); }} name={k} data-testid={`adresse-${k}`} /></Feld>
  );
  return (
    <DialogFormular absendenText="Speichern" onAbbrechen={onSchliessen}
      onAbsenden={async () => {
        const r = await a.adresseKorrigieren(orderId, {
          typ: adresse.typ, version: adresse.version, grund: grund || undefined, vorname: w.vorname ?? '', nachname: w.nachname ?? '', firma: w.firma || null,
          strasse: w.strasse ?? '', hausnummer: w.hausnummer ?? '', zusatz: w.zusatz || null, plz: w.plz ?? '', ort: w.ort ?? '', land: w.land ?? '',
        });
        onFertig(r.hinweise);
      }}>
      <p className="hinweis">Original aus dem Shop: {adresse.roh_adresse1}{adresse.roh_adresse2 ? `, ${adresse.roh_adresse2}` : ''}. Speichern erhöht die Version und schreibt das Protokoll.</p>
      <div className="raster-felder">
        {feld('vorname', 'Vorname')}{feld('nachname', 'Nachname')}{feld('firma', 'Firma')}
        {feld('strasse', 'Straße')}{feld('hausnummer', 'Hausnummer')}{feld('zusatz', 'Zusatz')}
        {feld('plz', 'PLZ')}{feld('ort', 'Ort')}{feld('land', 'Land (ISO)')}
      </div>
      <Feld label="Grund der Korrektur"><input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Hausnummer telefonisch erfragt" /></Feld>
    </DialogFormular>
  );
}

function KlaerfallMeldenDialog({ sendung, onSchliessen, onFertig }: { sendung: SendungDetail | null; onSchliessen: () => void; onFertig: () => void }) {
  const [grund, setGrund] = useState<KlaerfallGrund | ''>('');
  const [text, setText] = useState('');
  useEffect(() => { setGrund(''); setText(''); }, [sendung]);
  return (
    <Dialog offen={!!sendung} titel="Klärfall melden" onSchliessen={onSchliessen}>
      {sendung && (
        <DialogFormular absendenText="Klärfall melden" gefahr onAbbrechen={onSchliessen}
          onAbsenden={async () => { if (!grund) throw new Error('Grund ist Pflicht.'); await a.klaerfallMelden(sendung.id, grund, text); onFertig(); }}>
          <Feld label="Grund (Pflicht)">
            <select value={grund} onChange={(e) => setGrund(e.target.value as KlaerfallGrund)} autoFocus>
              <option value="">– bitte wählen –</option>
              {KLAERFALL_GRUENDE.map((g) => <option key={g} value={g}>{KLAERFALL_TEXT[g]}</option>)}
            </select>
          </Feld>
          <Feld label="Notiz"><input value={text} onChange={(e) => setText(e.target.value)} /></Feld>
        </DialogFormular>
      )}
    </Dialog>
  );
}

function LoesenFormular({ sendung, onAbbrechen, onFertig }: { sendung: SendungDetail | null; onAbbrechen: () => void; onFertig: () => void }) {
  const [text, setText] = useState('');
  if (!sendung) return null;
  return (
    <DialogFormular absendenText="Lösen" onAbbrechen={onAbbrechen} onAbsenden={async () => { if (text.trim().length < 3) throw new Error('Bitte kurz begründen.'); await a.klaerfallLoesen(sendung.id, text.trim()); onFertig(); }}>
      <p>Die Sendung geht zurück nach <SendungBadge status={sendung.status_vor_klaerfall ?? 'bereit'} />.</p>
      <Feld label="Was wurde geklärt? (Pflicht)"><input value={text} onChange={(e) => setText(e.target.value)} autoFocus /></Feld>
    </DialogFormular>
  );
}

function PartnerTrackingFormular({ sendungId, onAbbrechen, onFertig }: { sendungId: string; onAbbrechen: () => void; onFertig: () => void }) {
  const [carrier, setCarrier] = useState('dhl');
  const [nr, setNr] = useState('');
  return (
    <DialogFormular absendenText="Speichern" onAbbrechen={onAbbrechen} onAbsenden={async () => { await a.partnerTracking(sendungId, carrier, nr.trim()); onFertig(); }}>
      <Feld label="Versanddienst"><select value={carrier} onChange={(e) => setCarrier(e.target.value)}><option value="dhl">DHL</option><option value="dpd">DPD</option><option value="gls">GLS</option><option value="ups">UPS</option></select></Feld>
      <Feld label="Sendungsnummer"><input value={nr} onChange={(e) => setNr(e.target.value)} autoFocus /></Feld>
      <p className="hinweis">Speichern setzt die Sendung auf „Übergeben“ und schickt die Versandmail (Teilsendung, falls noch etwas offen ist).</p>
    </DialogFormular>
  );
}

export function GutschriftFormular({ orderId, vorschlag, onAbbrechen, onFertig }: { orderId: string; vorschlag: number; onAbbrechen: () => void; onFertig: (nummer: string) => void }) {
  const [betrag, setBetrag] = useState((vorschlag / 100).toFixed(2).replace('.', ','));
  const [grund, setGrund] = useState('');
  // R49 (W12): manuell nur für Erstattungen AUSSERHALB von WooCommerce. In Woo erstattet → Gutschrift entsteht automatisch.
  const [weg, setWeg] = useState<ErstattungswegT | ''>('');
  const [ausserhalb, setAusserhalb] = useState(false);
  const [doppelt, setDoppelt] = useState(false);
  return (
    <DialogFormular absendenText="Gutschrift anlegen" onAbbrechen={onAbbrechen} onAbsenden={async () => {
      const cents = Math.round(Number(betrag.replace(/\./g, '').replace(',', '.')) * 100);
      if (!cents || cents <= 0) throw new Error('Betrag angeben.');
      if (grund.trim().length < 3) throw new Error('Grund ist Pflicht.');
      if (!weg || !ausserhalb) throw new Error('Nur für Erstattungen außerhalb von WooCommerce: Weg wählen und bestätigen. Wurde in Woo erstattet, entsteht die Gutschrift automatisch.');
      const d = await gutschriftAnlegen(orderId, { betrag_cents: cents, grund: grund.trim(), erstattungsweg: weg, ausserhalb_woo: true, doppelt_bestaetigt: doppelt || undefined });
      onFertig(d.nummer);
    }}>
      <p className="hinweis">Nur wenn das Geld <strong>außerhalb von WooCommerce</strong> zurückging. Eine Erstattung in Woo erzeugt die Gutschrift selbst.</p>
      <Feld label="Betrag brutto (EUR)" hinweis="Höchstens Rechnungsbetrag abzüglich bisheriger Gutschriften"><input value={betrag} onChange={(e) => setBetrag(e.target.value)} inputMode="decimal" autoFocus /></Feld>
      <Feld label="Erstattungsweg (Pflicht)">
        <select value={weg} onChange={(e) => setWeg(e.target.value as ErstattungswegT | '')} data-testid="gs-weg">
          <option value="">– wählen –</option>
          <option value="ueberweisung">Überweisung</option>
          <option value="paypal_direkt">PayPal direkt (nicht über Woo)</option>
          <option value="bar">Bar</option>
          <option value="verrechnung">Verrechnung</option>
          <option value="sonstiges">Sonstiges</option>
        </select>
      </Feld>
      <Feld label="Grund (Pflicht)"><input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Kulanz per Überweisung" /></Feld>
      <label className="inline"><input type="checkbox" checked={ausserhalb} onChange={(e) => setAusserhalb(e.target.checked)} data-testid="gs-ausserhalb" /> Die Erstattung lief außerhalb von WooCommerce</label>
      <label className="inline"><input type="checkbox" checked={doppelt} onChange={(e) => setDoppelt(e.target.checked)} /> Zusätzlich zu einer Woo-Erstattung über denselben Betrag (zweite Auszahlung)</label>
    </DialogFormular>
  );
}
